import { useState, useEffect, useMemo } from 'react'
import { formatDate, formatDateTime } from '@utils/dateUtils'
import {
  Box,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Divider,
  InputAdornment,
  Stack,
} from '@mui/material'
import {
  Add,
  Check,
  Close,
  PlayArrow,
  AssignmentReturn,
  Cancel,
  OpenInNew,
  CheckCircle,
  Block,
  Print,
  FilterList,
  Clear,
} from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import { Link } from 'react-router-dom'
import { PageHeader, Button, DataTable, Card, PermissionGate, usePermissions, ExportButtons } from '@components/common'
import { toast } from 'react-toastify'
import api from '@services/api'

const STATUS_COLORS = {
  requested: 'warning',
  approved: 'info',
  rejected: 'error',
  in_use: 'primary',
  returned: 'success',
  cancelled: 'default',
}

const CONDITION_OPTIONS = [
  { value: 'good', label: 'Good' },
  { value: 'damaged', label: 'Damaged' },
  { value: 'needs_maintenance', label: 'Needs Maintenance' },
]

const columns = [
  { field: 'request_number', headerName: 'Request #', sortable: true, width: 130 },
  { field: 'vehicle_registration', headerName: 'Vehicle', sortable: true, width: 120 },
  { field: 'vehicle_name', headerName: 'Name', sortable: true },
  { field: 'destination', headerName: 'Destination', sortable: true },
  {
    field: 'departure_date',
    headerName: 'Departure',
    sortable: true,
    renderCell: (value) => value ? formatDate(value) : '-',
  },
  {
    field: 'status',
    headerName: 'Status',
    sortable: true,
    renderCell: (value) => (
      <Chip
        label={value?.replace('_', ' ').toUpperCase()}
        color={STATUS_COLORS[value] || 'default'}
        size="small"
      />
    ),
  },
  { field: 'requested_by_name', headerName: 'Requested By', sortable: true },
  {
    field: 'created_at',
    headerName: 'Date',
    sortable: true,
    renderCell: (value) => value ? formatDate(value) : '-',
  },
]

const initialFormData = {
  vehicle: '',
  purpose: '',
  destination: '',
  departure_date: '',
  expected_return_date: '',
  num_staff_on_board: 1,
  num_non_staff_on_board: 0,
  requester_rank: '',
}

const VehicleRequestsList = () => {
  const navigate = useNavigate()
  const { hasPermission } = usePermissions()
  const [requests, setRequests] = useState([])
  const [loading, setLoading] = useState(true)
  const [formOpen, setFormOpen] = useState(false)
  const [detailOpen, setDetailOpen] = useState(false)
  const [selectedRequest, setSelectedRequest] = useState(null)
  const [formData, setFormData] = useState(initialFormData)
  const [formLoading, setFormLoading] = useState(false)
  const [errors, setErrors] = useState({})
  const [showFilters, setShowFilters] = useState(false)

  // Filter states
  const [filters, setFilters] = useState({
    status: '',
  })

  // Action dialogs
  const [startTripOpen, setStartTripOpen] = useState(false)
  const [returnOpen, setReturnOpen] = useState(false)
  const [rejectOpen, setRejectOpen] = useState(false)
  const [approveOpen, setApproveOpen] = useState(false)
  const [mileageOut, setMileageOut] = useState('')
  const [mileageReturn, setMileageReturn] = useState('')
  const [conditionOnReturn, setConditionOnReturn] = useState('good')
  const [faultsEncountered, setFaultsEncountered] = useState('')
  const [returnNotes, setReturnNotes] = useState('')
  const [rejectionReason, setRejectionReason] = useState('')
  const [approverTitle, setApproverTitle] = useState('')
  const [approvalNotes, setApprovalNotes] = useState('')

  // Options for dropdowns
  const [vehicles, setVehicles] = useState([])

  // Export columns
  const exportColumns = useMemo(() => [
    { field: 'request_number', headerName: 'Request #' },
    { field: 'vehicle_registration', headerName: 'Vehicle' },
    { field: 'vehicle_name', headerName: 'Vehicle Name' },
    { field: 'destination', headerName: 'Destination' },
    { field: 'purpose', headerName: 'Purpose' },
    { field: 'departure_date', headerName: 'Departure', renderValue: (v) => v ? formatDate(v) : '' },
    { field: 'expected_return_date', headerName: 'Expected Return', renderValue: (v) => v ? formatDate(v) : '' },
    { field: 'status', headerName: 'Status', renderValue: (v) => v?.replace('_', ' ').toUpperCase() },
    { field: 'requested_by_name', headerName: 'Requested By' },
    { field: 'approved_by_name', headerName: 'Approved By' },
    { field: 'mileage_out', headerName: 'Mileage Out' },
    { field: 'mileage_return', headerName: 'Mileage Return' },
  ], [])

  const handleFilterChange = (field) => (event) => {
    setFilters(prev => ({ ...prev, [field]: event.target.value }))
  }

  const clearFilters = () => {
    setFilters({ status: '' })
  }

  const hasActiveFilters = filters.status

  const getActiveFilterLabels = () => {
    const labels = {}
    if (filters.status) labels.Status = filters.status.replace('_', ' ')
    return labels
  }

  const fetchRequests = async () => {
    try {
      setLoading(true)
      const params = {}
      if (filters.status) params.status = filters.status

      const response = await api.get('/assets/vehicle-requests/', { params })
      setRequests(response.data.results || response.data)
    } catch (error) {
      console.error('Error fetching vehicle requests:', error)
      toast.error('Failed to load vehicle requests')
    } finally {
      setLoading(false)
    }
  }

  const fetchVehicles = async () => {
    try {
      // Fetch only active vehicles - inactive vehicles cannot be booked
      const response = await api.get('/assets/vehicles/', { params: { is_active: true } })
      const vehicleList = response.data.results || response.data
      setVehicles(vehicleList)
    } catch (error) {
      console.error('Error fetching vehicles:', error)
      toast.error('Failed to load vehicles')
    }
  }

  const isVehicleAvailable = (vehicle) => {
    return vehicle.status === 'operational'
  }

  const getVehicleStatusLabel = (vehicle) => {
    if (vehicle.status === 'operational') return 'Available'
    return vehicle.status?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Unavailable'
  }

  useEffect(() => {
    fetchRequests()
  }, [filters])

  useEffect(() => {
    fetchVehicles()
  }, [])

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }))
    }
  }

  const handleSubmit = async () => {
    const newErrors = {}
    if (!formData.vehicle) newErrors.vehicle = 'Vehicle is required'
    if (!formData.purpose) newErrors.purpose = 'Purpose is required'
    if (!formData.destination) newErrors.destination = 'Destination is required'
    if (!formData.departure_date) newErrors.departure_date = 'Departure date is required'
    if (!formData.expected_return_date) newErrors.expected_return_date = 'Expected return date is required'

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors)
      return
    }

    setFormLoading(true)
    try {
      await api.post('/assets/vehicle-requests/', formData)
      toast.success('Vehicle request submitted successfully')
      fetchRequests()
      fetchVehicles()
      setFormOpen(false)
      setFormData(initialFormData)
    } catch (error) {
      console.error('Error creating request:', error)
      if (error.response?.data) {
        const backendErrors = {}
        Object.entries(error.response.data).forEach(([key, value]) => {
          backendErrors[key] = Array.isArray(value) ? value[0] : value
        })
        setErrors(backendErrors)
        toast.error('Please fix the errors in the form')
      } else {
        toast.error('Failed to create vehicle request')
      }
    } finally {
      setFormLoading(false)
    }
  }

  const handleApprove = async () => {
    try {
      await api.post(`/assets/vehicle-requests/${selectedRequest.id}/approve/`, {
        notes: approvalNotes,
        approver_title: approverTitle,
      })
      toast.success('Request approved successfully')
      fetchRequests()
      setDetailOpen(false)
      setApproveOpen(false)
      setApproverTitle('')
      setApprovalNotes('')
    } catch (error) {
      console.error('Error approving request:', error)
      toast.error(error.response?.data?.error || 'Failed to approve request')
    }
  }

  const handlePrint = (requestId) => {
    navigate(`/assets/vehicle-requests/${requestId}/print`)
  }

  const handleReject = async () => {
    if (!rejectionReason) {
      toast.error('Rejection reason is required')
      return
    }
    try {
      await api.post(`/assets/vehicle-requests/${selectedRequest.id}/reject/`, {
        reason: rejectionReason,
      })
      toast.success('Request rejected')
      fetchRequests()
      setDetailOpen(false)
      setRejectOpen(false)
      setRejectionReason('')
    } catch (error) {
      console.error('Error rejecting request:', error)
      toast.error(error.response?.data?.error || 'Failed to reject request')
    }
  }

  const handleStartTrip = async () => {
    if (!mileageOut) {
      toast.error('Mileage out is required')
      return
    }
    try {
      await api.post(`/assets/vehicle-requests/${selectedRequest.id}/start_trip/`, {
        mileage_out: parseInt(mileageOut),
      })
      toast.success('Trip started successfully')
      fetchRequests()
      fetchVehicles()
      setDetailOpen(false)
      setStartTripOpen(false)
      setMileageOut('')
    } catch (error) {
      console.error('Error starting trip:', error)
      toast.error(error.response?.data?.error || 'Failed to start trip')
    }
  }

  const handleRecordReturn = async () => {
    if (!mileageReturn) {
      toast.error('Return mileage is required')
      return
    }
    try {
      await api.post(`/assets/vehicle-requests/${selectedRequest.id}/record_return/`, {
        mileage_return: parseInt(mileageReturn),
        condition_on_return: conditionOnReturn,
        faults_encountered: faultsEncountered,
        return_notes: returnNotes,
      })
      toast.success('Return recorded successfully')
      fetchRequests()
      fetchVehicles()
      setDetailOpen(false)
      setReturnOpen(false)
      setMileageReturn('')
      setConditionOnReturn('good')
      setFaultsEncountered('')
      setReturnNotes('')
    } catch (error) {
      console.error('Error recording return:', error)
      toast.error(error.response?.data?.error || 'Failed to record return')
    }
  }

  const handleCancel = async () => {
    try {
      await api.post(`/assets/vehicle-requests/${selectedRequest.id}/cancel/`)
      toast.success('Request cancelled')
      fetchRequests()
      setDetailOpen(false)
    } catch (error) {
      console.error('Error cancelling request:', error)
      toast.error(error.response?.data?.error || 'Failed to cancel request')
    }
  }

  const handleRowClick = (row) => {
    setSelectedRequest(row)
    setDetailOpen(true)
  }

  const getActionButtons = () => {
    if (!selectedRequest) return null

    const printButton = (
      <Button
        startIcon={<Print />}
        variant="outlined"
        onClick={() => handlePrint(selectedRequest.id)}
      >
        Print
      </Button>
    )

    switch (selectedRequest.status) {
      case 'requested':
        return (
          <>
            {printButton}
            <PermissionGate permission="vehicle_requests.approve">
              <Button
                startIcon={<Check />}
                color="success"
                onClick={() => setApproveOpen(true)}
              >
                Approve
              </Button>
              <Button
                startIcon={<Close />}
                color="error"
                variant="outlined"
                onClick={() => setRejectOpen(true)}
              >
                Reject
              </Button>
            </PermissionGate>
            <Button
              startIcon={<Cancel />}
              variant="outlined"
              onClick={handleCancel}
            >
              Cancel
            </Button>
          </>
        )
      case 'approved':
        return (
          <>
            {printButton}
            <PermissionGate permission="vehicle_requests.start_trip">
              <Button
                startIcon={<PlayArrow />}
                onClick={() => setStartTripOpen(true)}
              >
                Start Trip
              </Button>
            </PermissionGate>
            <Button
              startIcon={<Cancel />}
              variant="outlined"
              onClick={handleCancel}
            >
              Cancel
            </Button>
          </>
        )
      case 'in_use':
        return (
          <>
            {printButton}
            <PermissionGate permission="vehicle_requests.record_return">
              <Button
                startIcon={<AssignmentReturn />}
                color="success"
                onClick={() => setReturnOpen(true)}
              >
                Record Return
              </Button>
            </PermissionGate>
          </>
        )
      case 'returned':
      case 'rejected':
      case 'cancelled':
        return printButton
      default:
        return null
    }
  }

  return (
    <Box>
      <PageHeader
        title="Vehicle Requests"
        subtitle="Request and track vehicle usage"
        breadcrumbs={[
          { label: 'Dashboard', path: '/dashboard' },
          { label: 'Assets', path: '/assets' },
          { label: 'Vehicle Requests' },
        ]}
        actions={
          <>
            <ExportButtons
              data={requests}
              columns={exportColumns}
              title="Vehicle Requests"
              filename="vehicle_requests"
              filters={getActiveFilterLabels()}
            />
            <Button
              variant="outlined"
              startIcon={<FilterList />}
              onClick={() => setShowFilters(!showFilters)}
              color={hasActiveFilters ? 'primary' : 'inherit'}
            >
              Filters {hasActiveFilters && `(1)`}
            </Button>
            <PermissionGate permission="vehicle_requests.request">
              <Button startIcon={<Add />} onClick={() => setFormOpen(true)}>
                New Request
              </Button>
            </PermissionGate>
          </>
        }
      />

      {/* Filters Section */}
      {showFilters && (
        <Card sx={{ mb: 2 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={filters.status}
                onChange={handleFilterChange('status')}
                label="Status"
              >
                <MenuItem value="">All Status</MenuItem>
                <MenuItem value="requested">Requested</MenuItem>
                <MenuItem value="approved">Approved</MenuItem>
                <MenuItem value="rejected">Rejected</MenuItem>
                <MenuItem value="in_use">In Use</MenuItem>
                <MenuItem value="returned">Returned</MenuItem>
                <MenuItem value="cancelled">Cancelled</MenuItem>
              </Select>
            </FormControl>

            {hasActiveFilters && (
              <Button
                variant="text"
                startIcon={<Clear />}
                onClick={clearFilters}
                size="small"
              >
                Clear
              </Button>
            )}
          </Stack>

          {/* Active filter chips */}
          {hasActiveFilters && (
            <Stack direction="row" spacing={1} sx={{ mt: 2 }} flexWrap="wrap">
              {Object.entries(getActiveFilterLabels()).map(([key, value]) => (
                <Chip
                  key={key}
                  label={`${key}: ${value}`}
                  size="small"
                  onDelete={clearFilters}
                />
              ))}
            </Stack>
          )}
        </Card>
      )}

      <Card>
        <DataTable
          columns={columns}
          data={requests}
          loading={loading}
          onRowClick={handleRowClick}
          searchPlaceholder="Search requests..."
          emptyMessage="No vehicle requests found"
        />
      </Card>

      {/* Create Request Dialog */}
      <Dialog open={formOpen} onClose={() => setFormOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>New Vehicle Request</DialogTitle>
        <DialogContent>
          {(errors.error || errors.detail || errors.non_field_errors) && (
            <Typography color="error" sx={{ mb: 2 }}>
              {errors.error || errors.detail || errors.non_field_errors}
            </Typography>
          )}
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <FormControl fullWidth error={Boolean(errors.vehicle)} required>
                <InputLabel>Vehicle</InputLabel>
                <Select
                  name="vehicle"
                  value={formData.vehicle}
                  onChange={handleChange}
                  label="Vehicle"
                  displayEmpty
                >
                  <MenuItem value="" disabled>
                    {vehicles.length === 0 ? 'Loading vehicles...' : 'Select a vehicle'}
                  </MenuItem>
                  {vehicles.map((vehicle) => (
                    <MenuItem
                      key={vehicle.id}
                      value={vehicle.id}
                      disabled={!isVehicleAvailable(vehicle)}
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        opacity: isVehicleAvailable(vehicle) ? 1 : 0.6,
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {isVehicleAvailable(vehicle) ? (
                          <CheckCircle fontSize="small" color="success" />
                        ) : (
                          <Block fontSize="small" color="disabled" />
                        )}
                        <span>{vehicle.registration_number} - {vehicle.name}</span>
                      </Box>
                      <Chip
                        label={getVehicleStatusLabel(vehicle)}
                        size="small"
                        color={isVehicleAvailable(vehicle) ? 'success' : 'default'}
                        sx={{ ml: 1 }}
                      />
                    </MenuItem>
                  ))}
                </Select>
                {errors.vehicle && (
                  <Typography variant="caption" color="error">{errors.vehicle}</Typography>
                )}
              </FormControl>
            </Grid>
            {formData.vehicle && (
              <Grid item xs={12}>
                <Link
                  to={`/assets/vehicles`}
                  target="_blank"
                  style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 14, color: '#1B5E7A' }}
                >
                  <OpenInNew fontSize="small" />
                  View vehicle details
                </Link>
              </Grid>
            )}

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Destination"
                name="destination"
                value={formData.destination}
                onChange={handleChange}
                error={Boolean(errors.destination)}
                helperText={errors.destination}
                required
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Departure Date & Time"
                name="departure_date"
                type="datetime-local"
                value={formData.departure_date}
                onChange={handleChange}
                error={Boolean(errors.departure_date)}
                helperText={errors.departure_date}
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Expected Return Date & Time"
                name="expected_return_date"
                type="datetime-local"
                value={formData.expected_return_date}
                onChange={handleChange}
                error={Boolean(errors.expected_return_date)}
                helperText={errors.expected_return_date}
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Purpose"
                name="purpose"
                value={formData.purpose}
                onChange={handleChange}
                error={Boolean(errors.purpose)}
                helperText={errors.purpose}
                multiline
                rows={3}
                required
              />
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                Passenger Information
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Number of Staff on Board"
                name="num_staff_on_board"
                type="number"
                value={formData.num_staff_on_board}
                onChange={handleChange}
                error={Boolean(errors.num_staff_on_board)}
                helperText={errors.num_staff_on_board}
                inputProps={{ min: 1 }}
                required
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Number of Non-Staff on Board"
                name="num_non_staff_on_board"
                type="number"
                value={formData.num_non_staff_on_board}
                onChange={handleChange}
                error={Boolean(errors.num_non_staff_on_board)}
                helperText={errors.num_non_staff_on_board}
                inputProps={{ min: 0 }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Rank/Position"
                name="requester_rank"
                value={formData.requester_rank}
                onChange={handleChange}
                error={Boolean(errors.requester_rank)}
                helperText={errors.requester_rank || 'Your rank or position (e.g., Senior Medical Officer)'}
                placeholder="e.g., Senior Medical Officer"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setFormOpen(false)}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} loading={formLoading}>
            Submit Request
          </Button>
        </DialogActions>
      </Dialog>

      {/* Request Detail Dialog */}
      <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Request {selectedRequest?.request_number}
          {selectedRequest && (
            <Chip
              label={selectedRequest.status?.replace('_', ' ').toUpperCase()}
              color={STATUS_COLORS[selectedRequest.status] || 'default'}
              size="small"
              sx={{ ml: 2 }}
            />
          )}
        </DialogTitle>
        <DialogContent>
          {selectedRequest && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} md={6}>
                <Typography variant="caption" color="text.secondary">Vehicle</Typography>
                <Typography>{selectedRequest.vehicle_registration} - {selectedRequest.vehicle_name}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="caption" color="text.secondary">Requested By</Typography>
                <Typography>{selectedRequest.requested_by_name}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="caption" color="text.secondary">Section/Unit</Typography>
                <Typography>{selectedRequest.department_name || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="caption" color="text.secondary">Destination</Typography>
                <Typography>{selectedRequest.destination}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">Purpose</Typography>
                <Typography>{selectedRequest.purpose}</Typography>
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <Typography variant="subtitle2">Passenger Information</Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="caption" color="text.secondary">Staff on Board</Typography>
                <Typography>{selectedRequest.num_staff_on_board || 1}</Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="caption" color="text.secondary">Non-Staff on Board</Typography>
                <Typography>{selectedRequest.num_non_staff_on_board || 0}</Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="caption" color="text.secondary">Total Passengers</Typography>
                <Typography>{(selectedRequest.num_staff_on_board || 1) + (selectedRequest.num_non_staff_on_board || 0)}</Typography>
              </Grid>
              {selectedRequest.requester_rank && (
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">Requester Rank/Position</Typography>
                  <Typography>{selectedRequest.requester_rank}</Typography>
                </Grid>
              )}

              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <Typography variant="subtitle2">Schedule</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="caption" color="text.secondary">Departure Date</Typography>
                <Typography>{selectedRequest.departure_date ? formatDateTime(selectedRequest.departure_date) : 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="caption" color="text.secondary">Expected Return</Typography>
                <Typography>{selectedRequest.expected_return_date ? formatDateTime(selectedRequest.expected_return_date) : 'N/A'}</Typography>
              </Grid>

              {selectedRequest.approved_by_name && (
                <>
                  <Grid item xs={12}>
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="subtitle2">Approval (Executive Director/Transport In-charge)</Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="caption" color="text.secondary">Approved By</Typography>
                    <Typography>{selectedRequest.approved_by_name}</Typography>
                  </Grid>
                  {selectedRequest.approver_title && (
                    <Grid item xs={12} md={6}>
                      <Typography variant="caption" color="text.secondary">Title</Typography>
                      <Typography>{selectedRequest.approver_title}</Typography>
                    </Grid>
                  )}
                  <Grid item xs={12} md={6}>
                    <Typography variant="caption" color="text.secondary">Approved Date</Typography>
                    <Typography>{selectedRequest.approved_date ? formatDateTime(selectedRequest.approved_date) : 'N/A'}</Typography>
                  </Grid>
                  {selectedRequest.approval_notes && (
                    <Grid item xs={12}>
                      <Typography variant="caption" color="text.secondary">Notes</Typography>
                      <Typography>{selectedRequest.approval_notes}</Typography>
                    </Grid>
                  )}
                </>
              )}

              {selectedRequest.rejection_reason && (
                <Grid item xs={12}>
                  <Typography variant="caption" color="error">Rejection Reason</Typography>
                  <Typography color="error">{selectedRequest.rejection_reason}</Typography>
                </Grid>
              )}

              {selectedRequest.actual_departure_date && (
                <>
                  <Grid item xs={12}>
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="subtitle2">Trip Details</Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="caption" color="text.secondary">Actual Departure</Typography>
                    <Typography>{formatDateTime(selectedRequest.actual_departure_date)}</Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="caption" color="text.secondary">Mileage Out</Typography>
                    <Typography>{selectedRequest.mileage_out?.toLocaleString()} km</Typography>
                  </Grid>
                </>
              )}

              {selectedRequest.actual_return_date && (
                <>
                  <Grid item xs={12}>
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="subtitle2">Return Details</Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="caption" color="text.secondary">Actual Return</Typography>
                    <Typography>{formatDateTime(selectedRequest.actual_return_date)}</Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="caption" color="text.secondary">Mileage Return</Typography>
                    <Typography>{selectedRequest.mileage_return?.toLocaleString()} km</Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="caption" color="text.secondary">Distance Traveled</Typography>
                    <Typography>{selectedRequest.distance_traveled?.toLocaleString()} km</Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="caption" color="text.secondary">Days Outside</Typography>
                    <Typography>{selectedRequest.days_outside_compound} days</Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="caption" color="text.secondary">Condition on Return</Typography>
                    <Chip
                      label={selectedRequest.condition_on_return_display || selectedRequest.condition_on_return}
                      color={selectedRequest.condition_on_return === 'good' ? 'success' : 'warning'}
                      size="small"
                    />
                  </Grid>
                  {selectedRequest.faults_encountered && (
                    <Grid item xs={12}>
                      <Typography variant="caption" color="error">Faults Encountered</Typography>
                      <Typography color="error">{selectedRequest.faults_encountered}</Typography>
                    </Grid>
                  )}
                  {selectedRequest.return_notes && (
                    <Grid item xs={12}>
                      <Typography variant="caption" color="text.secondary">Return Notes</Typography>
                      <Typography>{selectedRequest.return_notes}</Typography>
                    </Grid>
                  )}
                </>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setDetailOpen(false)}>
            Close
          </Button>
          {getActionButtons()}
        </DialogActions>
      </Dialog>

      {/* Start Trip Dialog */}
      <Dialog open={startTripOpen} onClose={() => setStartTripOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Start Trip</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Current Mileage (km)"
            type="number"
            value={mileageOut}
            onChange={(e) => setMileageOut(e.target.value)}
            sx={{ mt: 2 }}
            InputProps={{
              endAdornment: <InputAdornment position="end">km</InputAdornment>,
            }}
            required
          />
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setStartTripOpen(false)}>
            Cancel
          </Button>
          <Button onClick={handleStartTrip}>
            Start Trip
          </Button>
        </DialogActions>
      </Dialog>

      {/* Record Return Dialog */}
      <Dialog open={returnOpen} onClose={() => setReturnOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Record Vehicle Return</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Return Mileage (km)"
                type="number"
                value={mileageReturn}
                onChange={(e) => setMileageReturn(e.target.value)}
                InputProps={{
                  endAdornment: <InputAdornment position="end">km</InputAdornment>,
                }}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Condition on Return</InputLabel>
                <Select
                  value={conditionOnReturn}
                  onChange={(e) => setConditionOnReturn(e.target.value)}
                  label="Condition on Return"
                >
                  {CONDITION_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Faults Encountered"
                value={faultsEncountered}
                onChange={(e) => setFaultsEncountered(e.target.value)}
                multiline
                rows={2}
                placeholder="Describe any faults or issues encountered during the trip"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Return Notes"
                value={returnNotes}
                onChange={(e) => setReturnNotes(e.target.value)}
                multiline
                rows={2}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setReturnOpen(false)}>
            Cancel
          </Button>
          <Button onClick={handleRecordReturn} color="success">
            Record Return
          </Button>
        </DialogActions>
      </Dialog>

      {/* Rejection Dialog */}
      <Dialog open={rejectOpen} onClose={() => setRejectOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Reject Request</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Rejection Reason"
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
            multiline
            rows={3}
            sx={{ mt: 2 }}
            required
          />
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setRejectOpen(false)}>
            Cancel
          </Button>
          <Button onClick={handleReject} color="error">
            Reject
          </Button>
        </DialogActions>
      </Dialog>

      {/* Approval Dialog */}
      <Dialog open={approveOpen} onClose={() => setApproveOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Approve Vehicle Request</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                You are about to approve request <strong>{selectedRequest?.request_number}</strong> for vehicle <strong>{selectedRequest?.vehicle_registration}</strong>.
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Your Title"
                value={approverTitle}
                onChange={(e) => setApproverTitle(e.target.value)}
                placeholder="e.g., Executive Director, Transport In-charge"
                helperText="Your official title for authorization"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Approval Notes"
                value={approvalNotes}
                onChange={(e) => setApprovalNotes(e.target.value)}
                multiline
                rows={2}
                placeholder="Optional notes for this approval"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setApproveOpen(false)}>
            Cancel
          </Button>
          <Button onClick={handleApprove} color="success" startIcon={<Check />}>
            Approve Request
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default VehicleRequestsList
